<template>
  <div>
    <v-row class="align-center">
      <v-col cols="12" class="d-flex align-center flex-wrap">
        <div class="mr-auto">
          <gg-card-title class="pt-0 pb-0">{{ app }}</gg-card-title>
          <div v-if="server" class="apps__muted text-caption">
            On <router-link :to="{ name: 'hosting_kvm_info', params: { hostingid: serviceId } }" class="apps__link">{{ server.name }}</router-link>
            <template v-if="server.ip && server.ip !== '—'"> · {{ server.ip }}</template>
          </div>
        </div>
        <gg-btn tile outlined color="primary" :to="{ name: 'applications' }">Back to applications</gg-btn>
      </v-col>
    </v-row>

    <applications-operations :service-id="serviceId" @done="onOperationDone" />

    <gg-card>
      <gg-card-text class="pa-4">
        <div v-if="!server && !lookupFailed" class="py-6 text-center" role="status">
          <v-progress-circular indeterminate color="primary" size="32" width="3" aria-hidden="true" />
          <span class="apps__sr">Loading…</span>
        </div>
        <v-alert v-else-if="!server" type="warning" dense text class="mb-0 apps__alert">
          This server isn't a Docker server in your account.
        </v-alert>
        <p v-else-if="unavailable" class="apps__muted mb-0">{{ unavailable }}</p>
        <!-- The same Manage page as under the server's Docker tab. -->
        <kvm-docker-stacks v-else :service-id="serviceId" start="manage" :app="app"
                           :refresh-key="refreshKey" :finished-ops="finishedOps"
                           @operation="onOperation" @exit="$router.push({ name: 'applications' })" />
      </gg-card-text>
    </gg-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { addDockerOperation } from '@/helpers/dockerOperations'
import { kvmServices, dockerServerInfo, dockerServerUnavailable } from '@/helpers/dockerServers'
import KvmDockerStacks from '@/views/services/hosting/kvm/KvmDockerStacks'
import ApplicationsOperations from '@/views/applications/ApplicationsOperations'

/** One app's Manage page, reached from Applications (/applications/:serviceId/:app). */
export default {
  name: 'ApplicationsApp',
  components: { KvmDockerStacks, ApplicationsOperations },
  props: {
    serviceId: { type: String, required: true },
    app: { type: String, required: true },
  },
  data: () => ({
    server: null,
    lookupFailed: false,
    refreshKey: 0,
    finishedOps: [],
  }),
  computed: {
    ...mapGetters(['services', 'servicesPopulated']),
    unavailable () {
      return this.server ? dockerServerUnavailable(this.server) : null
    },
    billingRow () {
      return kvmServices(this.services).find(s => String(s.id) === this.serviceId) || null
    },
  },
  watch: {
    billingRow: { immediate: true, handler () { this.loadServer() } },
    servicesPopulated () { this.loadServer() },
  },
  methods: {
    async loadServer () {
      if (!this.billingRow) {
        // The services list loads with the session; only give up once it has.
        this.lookupFailed = !!this.servicesPopulated
        return
      }
      try {
        this.server = await dockerServerInfo(this.billingRow)
        this.lookupFailed = !this.server
      } catch (e) {
        this.lookupFailed = true
      }
    },
    onOperation (op) {
      addDockerOperation(this.serviceId, op)
    },
    onOperationDone ({ id }) {
      if (id) this.finishedOps = [...this.finishedOps, id]
      this.refreshKey++
    },
  },
}
</script>

<style scoped lang="scss">
@import '../services/hosting/kvm/kvm-ui';

.apps__sr { @include kvm-sr-only; }
.apps__muted { @include kvm-secondary-text; }
.apps__link { @include kvm-text-green-aa; }
.apps__alert ::v-deep .v-alert__content { color: #212121 !important; }
.theme--dark .apps__alert ::v-deep .v-alert__content { color: #eceff1 !important; }
</style>
